import useTranslate from "@hooks/use_translate"
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material"
import { alpha, useTheme, type Theme } from "@mui/material/styles"
import { useState } from "react"
import DiffViewer from "react-diff-viewer-continued"
import {
  FieldChange,
  FieldChangeKind,
  formatValue,
  summarizeChanges
} from "./migration.diff"
import { MigrationSample } from "./migration.models"

const mono = {
  fontFamily: "monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-all" as const
}

const KIND_COLOR: Record<FieldChangeKind, "success" | "error" | "warning"> = {
  ADDED: "success",
  REMOVED: "error",
  CHANGED: "warning"
}

const valueBox = (tone: "error" | "success", struck: boolean) => ({
  ...mono,
  display: "inline-block",
  maxHeight: 120,
  overflow: "auto",
  px: 0.75,
  py: 0.25,
  borderRadius: 0.5,
  bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.14),
  color: `${tone}.dark`,
  textDecoration: struck ? "line-through" : "none"
})

function ChangeRow({ change }: { change: FieldChange }) {
  const { translate } = useTranslate()
  return (
    <TableRow>
      <TableCell sx={{ ...mono, width: "30%", verticalAlign: "top" }}>
        {change.path}
      </TableCell>
      <TableCell sx={{ width: 90, verticalAlign: "top" }}>
        <Chip
          size="small"
          variant="outlined"
          color={KIND_COLOR[change.kind]}
          label={translate(`migration_change_${change.kind}`)}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          flexWrap="wrap"
          useFlexGap
        >
          {change.kind !== "ADDED" && (
            <Box
              sx={valueBox(
                "error",
                change.kind === "REMOVED" || change.kind === "CHANGED"
              )}
            >
              {formatValue(change.before)}
            </Box>
          )}
          {change.kind === "CHANGED" && (
            <Typography variant="caption" color="text.secondary">
              →
            </Typography>
          )}
          {change.kind !== "REMOVED" && (
            <Box sx={valueBox("success", false)}>
              {formatValue(change.after)}
            </Box>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}

function SampleCard({
  sample,
  hasPreview
}: {
  sample: MigrationSample
  hasPreview: boolean
}) {
  const { translate } = useTranslate()
  const theme = useTheme()
  const [showJson, setShowJson] = useState(false)
  const changed = sample.changes.length > 0

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={mono} color="text.secondary">
          _id: {sample.id}
        </Typography>
        {hasPreview && (
          <Chip
            size="small"
            color={changed ? "primary" : "default"}
            variant={changed ? "filled" : "outlined"}
            label={
              changed
                ? translate("migration_preview_sample_changes", {
                    count: sample.changes.length
                  })
                : translate("migration_preview_no_change")
            }
          />
        )}
        <Box flex={1} />
        {hasPreview && changed && (
          <Button size="small" onClick={() => setShowJson((open) => !open)}>
            {translate(
              showJson
                ? "migration_preview_hide_json"
                : "migration_preview_show_json"
            )}
          </Button>
        )}
      </Stack>

      {hasPreview && changed && (
        <Table size="small">
          <TableBody>
            {sample.changes.map((change) => (
              <ChangeRow key={change.path} change={change} />
            ))}
          </TableBody>
        </Table>
      )}

      {!hasPreview && (
        <Box sx={{ ...mono, bgcolor: "action.hover", p: 1, borderRadius: 1 }}>
          {sample.before}
        </Box>
      )}

      {showJson && (
        <Box sx={{ mt: 1, fontSize: 12, overflowX: "auto" }}>
          <DiffViewer
            oldValue={sample.before}
            newValue={sample.after}
            splitView
            showDiffOnly
            extraLinesSurroundingDiff={2}
            hideLineNumbers
            useDarkTheme={theme.palette.mode === "dark"}
            leftTitle={translate("migration_before")}
            rightTitle={translate("migration_after")}
          />
        </Box>
      )}
    </Box>
  )
}

type Props = {
  samples: MigrationSample[]
  /** False when the spec declares no `preview`: the sample is shown as it stands. */
  hasPreview: boolean
}

export default function SampleDiff({ samples, hasPreview }: Props) {
  const { translate } = useTranslate()
  const summary = summarizeChanges(samples.map((sample) => sample.changes))

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {!hasPreview && (
        <Alert severity="info">
          {translate("migration_preview_no_preview")}
        </Alert>
      )}
      {hasPreview && summary.length === 0 && (
        <Alert severity="warning">
          {translate("migration_preview_nothing_changes")}
        </Alert>
      )}
      {summary.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {translate("migration_preview_fields_summary")}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {summary.map((field) => (
              <Tooltip
                key={field.path}
                title={translate("migration_preview_field_help", {
                  kind: translate(`migration_change_${field.kind}`),
                  count: field.count,
                  total: samples.length
                })}
              >
                <Chip
                  size="small"
                  color={KIND_COLOR[field.kind]}
                  label={`${field.path} · ${field.count}/${samples.length}`}
                  sx={{ fontFamily: "monospace" }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>
      )}
      {samples.map((sample) => (
        <SampleCard key={sample.id} sample={sample} hasPreview={hasPreview} />
      ))}
    </Stack>
  )
}
